import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Calendar, ArrowRight, ArrowLeft, Newspaper } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useSiteContent } from '../context/SiteContentContext';
import { usePageTitle } from '../hooks/usePageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = 'https://www.tetralog.bg';

const TRUCK_IMAGES = {
  hero: '/images/truck-hero.jpg',
  snow: '/images/truck-snow.jpg',
  port: '/images/truck-port.jpg'
};

function formatDate(dateString, language) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const locale = language === 'bg' ? 'bg-BG' : 'en-US';
  return date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
}

// News List Component
function NewsList() {
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pageContent, setPageContent] = useState(null);
  const { language } = useSiteContent();
  
  // Set page title
  usePageTitle('news');

  // Default Translations
  const defaultContent = language === 'bg' ? {
    heroSubtitle: 'НОВИНИ И АКТУАЛИЗАЦИИ',
    heroTitle: 'Последни',
    heroTitleHighlight: 'новини',
    heroDescription: 'Бъдете в крак с последните новини, актуализации от компанията и индустриални прозрения от Tetralog.',
    noNewsTitle: 'Все още няма новини',
    noNewsText: 'Скоро ще има актуализации от Tetralog!',
    backToHome: 'Към началото',
    readMore: 'Прочети повече',
    ctaTitle: 'Останете свързани',
    ctaText: 'Следете ни в социалните мрежи за последните актуализации и новини.',
    ctaButton: 'Свържете се с нас'
  } : {
    heroSubtitle: 'NEWS & UPDATES',
    heroTitle: 'Latest',
    heroTitleHighlight: 'News',
    heroDescription: 'Stay updated with the latest news, company updates, and industry insights from Tetralog.',
    noNewsTitle: 'No News Yet',
    noNewsText: 'Check back soon for the latest updates from Tetralog!',
    backToHome: 'Back to Home',
    readMore: 'Read More',
    ctaTitle: 'Stay Connected',
    ctaText: 'Follow us on social media for the latest updates and news.',
    ctaButton: 'Contact Us'
  };

  // Build content from API or defaults
  const content = pageContent ? {
    heroSubtitle: language === 'bg' ? (pageContent.hero_subtitle_bg || defaultContent.heroSubtitle) : (pageContent.hero_subtitle_en || defaultContent.heroSubtitle),
    heroTitle: language === 'bg' ? (pageContent.hero_title_bg || defaultContent.heroTitle) : (pageContent.hero_title_en || defaultContent.heroTitle),
    heroTitleHighlight: language === 'bg' ? (pageContent.hero_title_highlight_bg || defaultContent.heroTitleHighlight) : (pageContent.hero_title_highlight_en || defaultContent.heroTitleHighlight),
    heroDescription: language === 'bg' ? (pageContent.hero_description_bg || defaultContent.heroDescription) : (pageContent.hero_description_en || defaultContent.heroDescription),
    noNewsTitle: language === 'bg' ? (pageContent.no_news_title_bg || defaultContent.noNewsTitle) : (pageContent.no_news_title_en || defaultContent.noNewsTitle),
    noNewsText: language === 'bg' ? (pageContent.no_news_text_bg || defaultContent.noNewsText) : (pageContent.no_news_text_en || defaultContent.noNewsText),
    backToHome: defaultContent.backToHome,
    readMore: defaultContent.readMore,
    ctaTitle: language === 'bg' ? (pageContent.cta_title_bg || defaultContent.ctaTitle) : (pageContent.cta_title_en || defaultContent.ctaTitle),
    ctaText: language === 'bg' ? (pageContent.cta_text_bg || defaultContent.ctaText) : (pageContent.cta_text_en || defaultContent.ctaText),
    ctaButton: language === 'bg' ? (pageContent.cta_button_bg || defaultContent.ctaButton) : (pageContent.cta_button_en || defaultContent.ctaButton)
  } : defaultContent;

  // Helper to get localized content
  const getLocalized = (article, field) => {
    const localizedField = `${field}_${language}`;
    return article[localizedField] || article[`${field}_en`] || article[field] || '';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [newsRes, contentRes] = await Promise.all([
          axios.get(`${API}/news`),
          axios.get(`${API}/news-page-content`)
        ]);
        setArticles(newsRes.data.articles);
        if (contentRes.data && Object.keys(contentRes.data).length > 0) {
          setPageContent(contentRes.data);
        }
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Show loader while data is loading
  if (isLoading) {
    return (
      <div className="page-loader">
        <div className="page-loader-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="news-page" className="pt-20 bg-slate-50">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <span className="overline mb-4 block">{content.heroSubtitle}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6">
            {content.heroTitle} <span className="text-[#C41E3A]">{content.heroTitleHighlight}</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl">{content.heroDescription}</p>
        </div>
      </section>

      {/* News Grid */}
      <section data-testid="news-grid" className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="spinner mx-auto"></div>
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-3xl">
              <Newspaper className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">{content.noNewsTitle}</h3>
              <p className="text-slate-500 mb-6">{content.noNewsText}</p>
              <Link to="/">
                <button className="btn-modern btn-primary btn-shimmer">
                  {content.backToHome}
                </button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article, index) => (
                <Card 
                  key={article.id}
                  data-testid={`news-card-${article.id}`}
                  className="bg-white border-slate-100 hover:shadow-xl transition-all duration-300 rounded-3xl overflow-hidden animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.image_url || TRUCK_IMAGES.hero}
                      alt={getLocalized(article, 'title')}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-3 py-1 bg-red-50 text-[#C41E3A] rounded-full text-xs font-medium">
                        {article.category}
                      </span>
                      <span className="flex items-center gap-1 text-slate-400 text-xs">
                        <Calendar className="w-3 h-3" />
                        {formatDate(article.created_at, language)}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2">
                      {getLocalized(article, 'title')}
                    </h3>
                    <p className="text-slate-500 text-sm mb-4 line-clamp-3">
                      {getLocalized(article, 'excerpt')}
                    </p>
                    <Link 
                      to={`/news/${article.slug}`}
                      className="inline-flex items-center text-[#C41E3A] text-sm font-medium hover:gap-2 transition-all"
                    >
                      {content.readMore} <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#C41E3A] to-[#A01830]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{content.ctaTitle}</h2>
          <p className="text-white/80 mb-8">{content.ctaText}</p>
          <Link to="/contact">
            <button className="btn-modern btn-light btn-lg">
              {content.ctaButton} <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}

// News Article Detail Component
function NewsDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { language } = useSiteContent();
  
  // Set page title for news article (will be updated dynamically)
  usePageTitle('news');

  // Translations
  const content = language === 'bg' ? {
    articleNotFound: 'Статията не е намерена',
    backToNews: 'Към новините',
    interestedTitle: 'Интересувате се от нашите услуги?',
    interestedText: 'Получете безплатна оферта за вашите транспортни нужди днес.',
    getQuote: 'Поискайте оферта'
  } : {
    articleNotFound: 'Article Not Found',
    backToNews: 'Back to News',
    interestedTitle: 'Interested in Our Services?',
    interestedText: 'Get a free quote for your transport needs today.',
    getQuote: 'Get a Quote'
  };

  // Helper to get localized content
  const getLocalized = (article, field) => {
    if (!article) return '';
    const localizedField = `${field}_${language}`;
    return article[localizedField] || article[`${field}_en`] || article[field] || '';
  };

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await axios.get(`${API}/news/${slug}`);
        setArticle(response.data);
      } catch (error) {
        console.error('Error fetching article:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  if (isLoading) {
    return (
      <div className="pt-20 min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="pt-20 min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">{content.articleNotFound}</h2>
          <Link to="/news">
            <button className="btn-modern btn-primary btn-shimmer">
              <ArrowLeft className="w-4 h-4" /> {content.backToNews}
            </button>
          </Link>
        </div>
      </div>
    );
  }

  const articleTitle = getLocalized(article, 'title');
  const articleExcerpt = getLocalized(article, 'excerpt');
  const articleImage = article.image_url || TRUCK_IMAGES.hero;
  const articleUrl = `${SITE_URL}/news/${article.slug}`;

  return (
    <div className="pt-20 bg-slate-50">
      {/* Dynamic SEO for this article */}
      <Helmet>
        <title>{articleTitle} | Tetralog Ltd.</title>
        <meta name="description" content={articleExcerpt} />
        
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={articleUrl} />
        <meta property="og:title" content={articleTitle} />
        <meta property="og:description" content={articleExcerpt} />
        <meta property="og:image" content={articleImage.startsWith('http') ? articleImage : `${SITE_URL}${articleImage}`} />
        <meta property="og:site_name" content="Tetralog Ltd." />
        <meta property="article:published_time" content={article.created_at} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={articleTitle} />
        <meta name="twitter:description" content={articleExcerpt} />
        <meta name="twitter:image" content={articleImage.startsWith('http') ? articleImage : `${SITE_URL}${articleImage}`} />
        
        {/* Canonical */}
        <link rel="canonical" href={articleUrl} />
      </Helmet>

      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px]">
        <img
          src={article.image_url || TRUCK_IMAGES.hero}
          alt={getLocalized(article, 'title')}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-[#C41E3A] text-white rounded-full text-xs font-medium">
                {article.category}
              </span>
              <span className="flex items-center gap-1 text-white/80 text-sm">
                <Calendar className="w-4 h-4" />
                {formatDate(article.created_at, language)}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white">
              {getLocalized(article, 'title')}
            </h1>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <Link 
            to="/news"
            className="inline-flex items-center text-[#C41E3A] text-sm font-medium mb-8 hover:gap-2 transition-all"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> {content.backToNews}
          </Link>
          
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm">
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              {getLocalized(article, 'excerpt')}
            </p>
            <div 
              className="prose prose-lg max-w-none prose-headings:text-slate-900 prose-p:text-slate-600"
              dangerouslySetInnerHTML={{ __html: getLocalized(article, 'content').replace(/\n/g, '<br />') }}
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h3 className="text-2xl font-bold text-slate-900 mb-4">{content.interestedTitle}</h3>
          <p className="text-slate-500 mb-6">{content.interestedText}</p>
          <Link to="/contact?quote=true#quote-form">
            <button className="btn-modern btn-primary btn-shimmer">
              {content.getQuote} <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}

export default function NewsPage() {
  const { slug } = useParams();
  
  if (slug) {
    return <NewsDetail />;
  }
  
  return <NewsList />;
}
